import React, { useRef, useEffect, useState, Suspense } from 'react';
import { useGLTF, TransformControls, Html, useAnimations } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { EnhancedTransformControls } from './EnhancedTransformControls';
import { useTransform } from '../../../../contexts/TransformContext';
import { createDefaultPipeline, modelHasLights } from '../utils/modelProcessors';

// Error cube geometry and material
const errorGeometry = new THREE.BoxGeometry(1, 1, 1);
const errorMaterial = new THREE.MeshStandardMaterial({
    color: '#ff0000',
    transparent: true,
    opacity: 0.8,
    roughness: 0.5,
    metalness: 0.5
});

// Feature flags for enabling/disabling model processing
const MODEL_PROCESSING_ENABLED = true; // Global switch for the feature
const AUTO_PROCESS_LIGHTS = true; // Auto-detect and process lights

// Fallback error component
function ErrorBox() {
    return (
        <mesh geometry={errorGeometry} material={errorMaterial}>
            <Html
                position={[0, 1.5, 0]}
                center
                style={{
                    background: 'rgba(0, 0, 0, 0.75)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    color: 'white',
                    fontSize: '12px',
                    pointerEvents: 'none',
                    userSelect: 'none',
                }}
            >
                Failed to load model
            </Html>
        </mesh>
    );
}

function SafeModel({ url, onError, processingOptions }) {
    const [hasError, setHasError] = useState(false);
    const [hasLights, setHasLights] = useState(false);
    const gltf = useGLTF(url, undefined, () => {
        console.warn('Failed to load model:', url);
        setHasError(true);
        onError?.();
    });
    
    const { actions } = useAnimations(gltf.animations, gltf.scene);

    useEffect(() => {
        if (!gltf.scene) return;
        
        console.log('Model loaded:', url);
        
        // Check if model has lights
        const containsLights = modelHasLights(gltf.scene);
        setHasLights(containsLights);
        
        if (containsLights) {
            console.log('Model contains lights:', url);
        }
        
        // Apply standard material normalization to all models
        try {
            console.log('Applying material normalization to model:', url);
            
            // Normalize all materials for consistent appearance
            gltf.scene.traverse((child) => {
                if (child.isMesh && child.material) {
                    // Handle both single materials and material arrays
                    const materials = Array.isArray(child.material) ? child.material : [child.material];
                    
                    materials.forEach((material) => {
                        if (material.isMeshStandardMaterial || material.isMeshPhysicalMaterial) {
                            // Ensure materials work well with our lighting setup
                            material.envMapIntensity = 0.3; // Subtle environment reflections
                            
                            // Intelligently brighten only dark materials
                            if (material.color) {
                                const brightness = (material.color.r + material.color.g + material.color.b) / 3;
                                if (brightness < 0.2) {
                                    // Boost very dark materials (Python-generated)
                                    material.color.multiplyScalar(1.8);
                                } else if (brightness < 0.4) {
                                    // Boost moderately dark materials
                                    material.color.multiplyScalar(1.4);
                                } else if (brightness < 0.6) {
                                    // Slight boost for medium materials
                                    material.color.multiplyScalar(1.1);
                                }
                                // Leave bright materials (>0.6) untouched for Blender models
                            }
                            
                            // Make materials more responsive to ambient light
                            material.roughness = 0.8; // Higher roughness = more diffuse = less directional dependency
                            material.metalness = Math.min(material.metalness, 0.1); // Reduce metalness for better ambient response
                            
                            // Set toneMapped to false for consistent color representation
                            material.toneMapped = false;
                            
                            // Boost emissive slightly for self-illumination effect
                            if (material.emissive) {
                                material.emissive.multiplyScalar(1.2);
                            }
                        }
                    });
                }
            });
            
        } catch (error) {
            console.error('Error applying material normalization:', error);
        }
        
        // Apply model processing if enabled and needed
        if (MODEL_PROCESSING_ENABLED) {
            const shouldProcessLights = 
                // Process if explicitly requested
                (processingOptions?.processLights === true) || 
                // Or auto-process if enabled and model has lights
                (AUTO_PROCESS_LIGHTS && containsLights);
                
            if (shouldProcessLights) {
                try {
                    console.log('Applying light processing to model:', url);
                    const pipeline = createDefaultPipeline({
                        processLights: true,
                        lightOptions: {
                            disableLights: processingOptions?.disableLights !== false,
                            ...processingOptions?.lightOptions
                        }
                    });
                    
                    // Process in-place (non-invasive, only affects lights)
                    pipeline.process(gltf.scene);
                } catch (error) {
                    console.error('Error applying model processing:', error);
                }
            }
        }
        
        // Handle animations
        console.log('Available animations:', Object.keys(actions));
        
        Object.values(actions).forEach(action => {
            if (action) {
                action.reset().play();
            }
        });

        return () => {
            Object.values(actions).forEach(action => {
                if (action) {
                    action.stop();
                }
            });
        };
    }, [actions, gltf.scene, url, processingOptions]);

    if (hasError) {
        return <ErrorBox />;
    }

    return <primitive object={gltf.scene} />;
}

export function Model3D({
    asset,
    position,
    rotation,
    scale,
    selected,
    onSelect,
    id,
    onTransformEnd,
    isShowMode,
    canInteract = true,
    processingOptions,
    onHover
}) {
    const modelRef = useRef();
    const modelUrl = asset?.modelUrl || asset?.url;
    const { transformMode } = useTransform();

    // Convert inputs to THREE objects if they aren't already
    const positionVector = position instanceof THREE.Vector3
        ? position
        : new THREE.Vector3().fromArray(Array.isArray(position) ? position : [0, 1, 0]);

    const rotationEuler = rotation instanceof THREE.Euler
        ? rotation
        : new THREE.Euler().fromArray(Array.isArray(rotation) ? rotation : [0, 0, 0]);

    const scaleVector = scale instanceof THREE.Vector3
        ? scale
        : new THREE.Vector3().fromArray(Array.isArray(scale) ? scale : [0.5, 0.5, 0.5]);

    // Update position and rotation when not selected
    useEffect(() => {
        if (!selected && modelRef.current) {
            modelRef.current.position.copy(positionVector);
            modelRef.current.rotation.copy(rotationEuler);
            modelRef.current.scale.copy(scaleVector);
        }
    }, [position, rotation, scale, selected, positionVector, rotationEuler, scaleVector]);

    // Get model-specific processing options
    const modelProcessingOptions = {
        // Use asset-specific options if provided
        ...asset?.processingOptions,
        // Override with component-specific options
        ...processingOptions
    };

    return (
        <>
            <group
                ref={modelRef}
                position={positionVector}
                rotation={rotationEuler}
                scale={scaleVector}
                onClick={(e) => {
                    e.stopPropagation();
                    // Only allow click interaction in edit mode
                    if (canInteract && !isShowMode) {
                        onSelect?.(id);
                    }
                }}
                onPointerOver={(e) => {
                    e.stopPropagation();
                    // In view mode with asset, notify parent of hover for E interaction
                    if (isShowMode && asset?.url && onHover) {
                        onHover(id);
                    }
                }}
                onPointerOut={(e) => {
                    e.stopPropagation();
                    // Clear hover state
                    if (isShowMode && asset?.url && onHover) {
                        onHover(null);
                    }
                }}
            >
                <Suspense fallback={null}>
                    <SafeModel
                        url={modelUrl || '/models/placeholder.glb'}
                        onError={() => console.warn('Model failed to load:', modelUrl)}
                        processingOptions={modelProcessingOptions}
                    />
                </Suspense>
            </group>

            {selected && !isShowMode && (
                <>
                    <EnhancedTransformControls
                        object={modelRef.current}
                        id={id}
                        onTransformEnd={onTransformEnd}
                        enabled={!isShowMode}
                        showHelpers={true}
                        uniformScale={true}
                        minScale={0.0001}  // Allow extremely small scales (0.01% of original size)
                        maxScale={Infinity}    // Allow unlimited scaling for 3D models
                    />
                    <Html
                        position={[0, 2, 0]}
                        center
                        style={{
                            background: 'rgba(0, 0, 0, 0.9)',
                            padding: '8px 14px',
                            borderRadius: '6px',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '600',
                            pointerEvents: 'none',
                            userSelect: 'none',
                            opacity: 1,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
                        }}
                    >
                        Press T to Move • R to Rotate • S to Scale
                    </Html>
                </>
            )}
        </>
    );
}

// Preload placeholder model
useGLTF.preload('/models/placeholder.glb');